import React from 'react';
import { Link } from 'react-router-dom';

import CommonAnimatedLoader from '../../components/common/animated/loader';
import { useOrder } from '../../hooks/use-order';
import OrderStatusBadge from '../../components/orders/status-badge';
import Badge from '../../components/common/badge';
import { getAgeFromBirthDate } from '../../services/formatter/date';
import { buildShopifyOnboardingUrl } from '../../utils/shopify';

function OrdersResultsDetailsPageComponent() {
  const { order } = useOrder();

  if (!order) {
    return <CommonAnimatedLoader />;
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';

    try {
      const date = new Date(dateString);

      return date.toLocaleString('en-GB', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  const formatCurrency = (amount: number | string, currencyCode: string) => {
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;

    return `${numAmount.toFixed(2)} ${currencyCode}`;
  };

  return (
    <div className="space-y-6">
      {/* Order Information */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
          <h3 className="text-lg leading-6 font-medium text-gray-900">Order Information</h3>
        </div>
        <div className="border-t border-gray-200">
          <dl>
            <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Order ID</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{order.order.id}</dd>
            </div>
            <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Order Hash</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2 font-mono">{order.order.hash}</dd>
            </div>
            {order.order.shopifyOrderId && !order.order.customerId && (
              <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Onboarding Link</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  {(() => {
                    const url = buildShopifyOnboardingUrl(
                      order.order.shopifyOrderId,
                      order.order.email,
                      order.order.region || 'eu',
                    );

                    return (
                      <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-teal-600 hover:text-teal-900 break-all"
                      >
                        {url} ↗
                      </a>
                    );
                  })()}
                </dd>
              </div>
            )}
            {!order.order.surveyFinished && !order.order.shopifyOrderId && (
              <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Questionnaire Link</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  {(() => {
                    const siteAppUrls: Record<number, string> = {
                      1: 'https://app.allergomedica.lt',
                      2: 'https://app.allergomedica.pl',
                      3: 'https://app.foodid.lt',
                      4: 'https://app.foodid.pl',
                      5: 'https://app.allergomedica.lv',
                      6: 'https://app.foodid.lv',
                      8: 'https://app.allergomedica.eu',
                      10: 'https://app.self.co',
                    };
                    const baseUrl = siteAppUrls[order.order.siteId] || 'https://app.allergomedica.lt';
                    const url = `${baseUrl}/questionnaire/ordered/${order.order.hash}/${order.order.productId}`;

                    return (
                      <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-indigo-600 hover:text-indigo-900 break-all"
                      >
                        {url} ↗
                      </a>
                    );
                  })()}
                </dd>
              </div>
            )}
            <div
              className={`${
                order.order.surveyFinished ? 'bg-gray-50' : 'bg-white'
              } px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6`}
            >
              <dt className="text-sm font-medium text-gray-500">Status</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                <OrderStatusBadge status={order.order.status} />
              </dd>
            </div>
            <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Product</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{order.order.productTitle || order.order.productKey}</span>
                  {order.order.isConsultation && <Badge text="+ Consultation" status="primary" />}
                  {order.order.isGift && <Badge text="Gift" status="warning" />}
                  {order.order.isAsit && <Badge text="ASIT" status="success" />}
                </div>
              </dd>
            </div>
            <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Created At</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{formatDate(order.order.createdAt)}</dd>
            </div>
            <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Paid At</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{formatDate(order.order.paidAt)}</dd>
            </div>
            <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Site</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{order.order.siteTitle}</dd>
            </div>
            {order.order.shopifyOrderId && (
              <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Shopify Order</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  <a
                    href={`https://admin.shopify.com/store/self-co-3/orders/${order.order.shopifyOrderId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-indigo-600 hover:text-indigo-900"
                  >
                    #{order.order.shopifyOrderNumber || order.order.shopifyOrderId} ↗
                  </a>
                </dd>
              </div>
            )}
          </dl>
        </div>
      </div>

      {/* Customer Information */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
          <h3 className="text-lg leading-6 font-medium text-gray-900">Customer Information</h3>
        </div>
        <div className="border-t border-gray-200">
          <dl>
            <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Name</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {order.order.researchMemberId
                  ? `DI${order.order.researchMemberId}`
                  : `${order.order.firstName} ${order.order.lastName}`}
              </dd>
            </div>
            <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Email</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{order.order.email}</dd>
            </div>
            {order.order.thrivaUserId && (
              <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Thriva User ID</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2 font-mono">
                  {order.order.thrivaUserId}
                </dd>
              </div>
            )}
            <div
              className={`${
                order.order.thrivaUserId ? 'bg-white' : 'bg-gray-50'
              } px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6`}
            >
              <dt className="text-sm font-medium text-gray-500">Phone</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{order.order.phone}</dd>
            </div>
            {order.order.personCode && (
              <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Person Code</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{order.order.personCode}</dd>
              </div>
            )}
          </dl>
        </div>
      </div>

      {/* Patient Information */}
      {(order.order.patientId || order.order.patientName) && (
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
            <h3 className="text-lg leading-6 font-medium text-gray-900">Patient Information</h3>
          </div>
          <div className="border-t border-gray-200">
            <dl>
              {order.order.patientName && (
                <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Patient</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    <div className="flex items-center gap-2">
                      {order.order.patientHash ? (
                        <Link
                          to={`/patients/preview/${order.order.patientHash}`}
                          className="text-indigo-600 hover:text-indigo-900 font-medium"
                        >
                          {order.order.patientName} {order.order.patientLastName}
                        </Link>
                      ) : (
                        <span>
                          {order.order.patientName} {order.order.patientLastName}
                        </span>
                      )}
                      {order.order.patientIsChild && <Badge text="Child" status="primary" />}
                    </div>
                  </dd>
                </div>
              )}
              {(order.order.patientAge !== undefined || order.order.patientBirthDate) && (
                <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Age</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    {order.order.patientBirthDate
                      ? getAgeFromBirthDate(order.order.patientBirthDate)
                      : order.order.patientAge}{' '}
                    {(order.order.patientBirthDate
                      ? getAgeFromBirthDate(order.order.patientBirthDate)
                      : Number(order.order.patientAge)) === 1
                      ? 'year'
                      : 'years'}{' '}
                    old
                  </dd>
                </div>
              )}
              {order.order.childCode && (
                <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Child Code</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{order.order.childCode}</dd>
                </div>
              )}
            </dl>
          </div>
        </div>
      )}

      {/* Payment Information */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
          <h3 className="text-lg leading-6 font-medium text-gray-900">Payment Information</h3>
        </div>
        <div className="border-t border-gray-200">
          <dl>
            <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Subtotal</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {formatCurrency(order.order.subtotal, order.order.currencyCode)}
              </dd>
            </div>
            {order.order.discount > 0 && (
              <>
                <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Discount</dt>
                  <dd className="mt-1 text-sm text-red-600 sm:mt-0 sm:col-span-2">
                    -{formatCurrency(order.order.discount, order.order.currencyCode)}
                  </dd>
                </div>
                {order.order.couponCode && (
                  <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">Coupon Code</dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2 font-mono">
                      {order.order.couponCode}
                    </dd>
                  </div>
                )}
              </>
            )}
            <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Total Amount</dt>
              <dd className="mt-1 text-sm font-bold text-gray-900 sm:mt-0 sm:col-span-2">
                {formatCurrency(order.order.amount, order.order.currencyCode)}
              </dd>
            </div>
            {order.order.paymentMethod && (
              <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Payment Method</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{order.order.paymentMethod}</dd>
              </div>
            )}
          </dl>
        </div>
      </div>

      {/* Blood Sample Information */}
      {order.order.isBloodRegistered && (
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
            <h3 className="text-lg leading-6 font-medium text-gray-900">Blood Sample Information</h3>
          </div>
          <div className="border-t border-gray-200">
            <dl>
              <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Blood Registered</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  <Badge text="Yes" status="success" />
                </dd>
              </div>
            </dl>
          </div>
        </div>
      )}

      {/* Shipping Address */}
      {(order.order.customerAddress || order.order.parcelTerminalName) && (
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
            <h3 className="text-lg leading-6 font-medium text-gray-900">Shipping Address</h3>
          </div>
          <div className="border-t border-gray-200">
            <dl>
              {order.order.customerAddress && (
                <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Address</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    <div>{order.order.customerAddress}</div>
                    <div>
                      {order.order.customerCity}
                      {order.order.customerState && `, ${order.order.customerState}`}
                      {order.order.customerZipCode && ` ${order.order.customerZipCode}`}
                    </div>
                    {order.order.customerCountryCode && <div>{order.order.customerCountryCode}</div>}
                  </dd>
                </div>
              )}
              {order.order.parcelTerminalName && (
                <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Parcel Terminal</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    <div className="font-medium">{order.order.parcelTerminalName}</div>
                    {order.order.parcelTerminalAddress && <div>{order.order.parcelTerminalAddress}</div>}
                    {order.order.parcelTerminalCity && <div>{order.order.parcelTerminalCity}</div>}
                  </dd>
                </div>
              )}
            </dl>
          </div>
        </div>
      )}

      {/* Fulfillment - Thriva */}
      {order.order.thrivaOrderId && (
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200 flex items-center justify-between">
            <h3 className="text-lg leading-6 font-medium text-gray-900">Fulfillment</h3>
            <Badge text="Fulfilled by Thriva" status="primary" />
          </div>
          <div className="border-t border-gray-200">
            <dl>
              <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Thriva Order ID</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2 font-mono">
                  {order.order.thrivaOrderId}
                </dd>
              </div>
              <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Thriva User ID</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2 font-mono">
                  {order.order.thrivaUserId}
                </dd>
              </div>
              <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Sent At</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  {formatDate(order.order.thrivaSentAt)}
                </dd>
              </div>
            </dl>
          </div>
        </div>
      )}
    </div>
  );
}

export default OrdersResultsDetailsPageComponent;
